import { Word } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as expression from '../expression'

import { Query } from './query'
import { Ref } from './ref'
import { Target } from './target'

/**
 * TODO:
 */
// NOTE: Subquery should always be assignable to Query
export interface Subquery<out T = any>
  extends Required.Semi<Omit<Query<T>, 'variables'>, 'target'> {
  /**
   * Subqueries must connect to the parent query directly or indirectly through
   * other subqueries. If connecting indirectly, this property identifies which
   * subquery this subquery connects to
   *
   * If given, the given subquery can be referenced in this subquery's expressions
   *
   * @default subquery connects to the parent query itself
   * @example
   * // Connects subquery 'b' to the query through subquery 'a'
   * {
   *   ...
   *   subqueries: {
   *     a: {
   *       // Is linkable to the parent query
   *       target: ...,
   *       ...
   *     },
   *     b: {
   *       using: 'a',
   *       // Is linkable to `a`
   *       target: ...,
   *       ...
   *     },
   *   }
   * }
   */
  using?: Subquery.Name
  /**
   * Connects this subquery to the parent query through a reference attribute
   *
   * Leave this undefined if the subquery is for an ancestor or descendant of
   * the query entity
   *
   * @default assumes ancestor/descendant relationship
   * @example
   * // TODO:
   */
  ref?: Ref
}
export namespace Subquery {
  export const isSubquery: TypeChecker<Subquery> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.intersection(
        Query.isQuery,
        TypeChecker.Factory.object<Difference<Subquery, Query>>({
          ref: TypeChecker.Factory.optional(Ref.isRef),
          target: Target.isTarget,
          using: TypeChecker.Factory.optional(Name.isName),
        }),
      ),
  ).usingAlias('Subquery')
  export const assertIsSubquery: TypeAsserter<Subquery> =
    TypeAsserter.Factory.from(isSubquery)

  /**
   * All subqueries in the given query, including nested subqueries
   */
  export function allIn(query: Query): Subquery[] {
    return directlyIn(query).flatMap(subquery => [subquery, ...allIn(subquery)])
  }

  /**
   * All subqueries directly in the given query (i.e. not including nested subqueries)
   */
  export function directlyIn(query: Query): Subquery[] {
    const { subqueries = {} } = query
    // Exclude nullish values from subqueries, as they are represented as
    // Partial records.
    return Name.declaredIn(query)
      .map(alias => subqueries[alias])
      .filter(TypeChecker.isNotNullish.raw)
  }

  /**
   * TODO:
   */
  export type Lookup = Partial<Record<Name, Subquery>>
  export namespace Lookup {
    export const isLookup: TypeChecker<Lookup> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.record(Name.isName, isSubquery),
    )
  }

  /**
   * TODO:
   */
  export type Name = Word
  export namespace Name {
    export const isName: TypeChecker<Name> =
      Word.isWord.usingAlias('Subquery.Name')

    /**
     * Aliases of subqueries directly declared in the given query (i.e. not
     * including nested subqueries)
     */
    export function declaredIn(query: Query): Name[] {
      const { subqueries = {} } = query
      return Object.keys(subqueries).filter(
        alias => subqueries[alias] !== undefined,
      )
    }

    /**
     * Indicates the given alias is declared in the given query
     */
    export function isDeclaredIn(query: Query, alias: Name): boolean {
      return !!query.subqueries?.[alias]
    }

    /**
     * Aliases of subqueries directly referenced in the given query (i.e. not
     * including nested subqueries)
     *
     * __NOTE:__ done as
     * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function* generator}
     * since this is traversing all expressions in the query
     */
    export function* referencedIn(query: Query): Generator<Name> {
      // TODO: Deduplicate by having a set and checking if alias is already in
      // set before yielding
      for (const _expression of expression.Expression.allFrom(query)) {
        if (expression.fn.definitions.GetSubquery.isGetSubquery(_expression)) {
          yield _expression[expression.fn.Key.GetSubquery]
        }
      }
    }

    /**
     * Aliases of subqueries directly referenced in the given query (i.e. not
     * including nested subqueries)
     */
    export function isReferencedIn(query: Query, name: Name): boolean {
      for (const referencedAlias of referencedIn(query)) {
        if (referencedAlias === name) {
          return true
        }
      }
      return false
    }
  }
}
